import { HttpClient } from '../httpClient/HttpClient';
import {
    ICreateUserRequest,
    IGetUserByEmailRequest,
    IGrade,
    IRole,
    ISponsoredAds,
    ISponsoredAdsUpper,
    IUpdateUserProfileRequest,
    IUpdateUserRequest,
    IUserDetail,
    IUserPageAccess,
    IUserPaginationResponse,
    IUserRegistrationRequest,
} from '../types/manage';
import { ICreateRegionRequest, IGetRegionsForTenantRequest, IRegionDetail, IRegionPaginationResponse, ITenantDetail, IUpdateRegionRequest, } from '../types/regions';
import { ICreateSchoolRequest, ISchoolDetail, ISchoolPaginationResponse, ISchoolsForRegionRequest, IUpdateSchoolRequest } from '../types/schools';
import { IYouthDashboardInfo } from '../types/youths';
import { indexedDBService } from './indexdb-service';
import { IUsersForSchool, IUsersForSchoolPaginatedResponse } from '../types/SchoolCoordinator';
import { IAdminDashboardData, ICaseDetail, ICaseDetailInformation, IJudgeAssignmentRequest, IJudgeDetail, IJudgeSubmissionRequest } from '../types/judge';
import { ICaseDetailExpressionRequest, IExpressionRequest, IRequestedExpressions, ISortingExpressionRequest } from '../types/expression';

/**
 * @category user
 * @remarks Gets all of the user information for the currently logged in user by their email.
 * @returns - Currently logged in user's information {@link IUserDetail}
 */
export const GetUser = async () => {
    try {
        const instance = HttpClient();

        let emailString = localStorage.getItem("email");

        if (!emailString)
            emailString = "";

        let payload: IGetUserByEmailRequest = { email: emailString };

        let response = await instance.post('/Management/GetUser', payload);
        const user: IUserDetail = response.data;

        await indexedDBService.setItem("General", "User", user);
        return user;
    } catch (error) {
        if (!navigator.onLine) {
            const indexStorageData = await indexedDBService.getItem("General", "User");
            return indexStorageData || [];
        } else {
            throw error;
        }
    }
};

/**
 * @category user
 * @remarks The page access information for the logged in user. Used to tell which pages the User
 * should be able to see.
 * @returns - returns array containing the logged in users page access for every page. {@link IUserPageAccess}
 */
export const GetUserPageAccess = async () => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/Management/GetUserPageAccess');

        const pageaccess: { [key: string]: IUserPageAccess; } = response.data
        await indexedDBService.setItem("General", "UserPageAccess", pageaccess);

        return pageaccess;
    } catch (error) {
        if (!navigator.onLine) {
            const indexStorageData = await indexedDBService.getItem("General", "UserPageAccess");
            return indexStorageData || [];
        } else {
            throw error;
        }
    }
};

/**
 * @category user
 * @remarks Will return every user the currently logged in user can see and detailed profile information about them.
 * @returns - An array of user information based on the current logged in user's role. {@link IUserDetail}
 */
export const GetUserAdminList = async (expression: ISortingExpressionRequest, region?) => {
    try {
        const instance = HttpClient();
        let response;
        if (region) {
            response = await instance.post('/Management/GetAllUserInformation?regionFilter=' + region, expression)
        }
        else {
            response = await instance.post('/Management/GetAllUserInformation', expression)
        }
        const userList: IUserPaginationResponse = response.data;
        return userList;
    } catch (error) {
        throw error;
    }
}

/**
 * @category youth
 * @remarks Used by a Youth when registering for the first time will Create their user in the user table.
 * @param request - Request containing the information for a new Youth Registering themselves. {@link IUserRegistrationRequest}
 * @returns - message of success or failure
 */
export const registerUser = async (request: IUserRegistrationRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.post('/Public/RegisterUser', request);
        const message: string = response.data;
        return message;
    } catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category user
 * @remarks Sends all the information about a new user through the api and creates the user in the database.
 * @param request - information about a User entered when creating one as an admin from the UserAdmin pages. {@link ICreateUserRequest}
 * @returns  - Message of success or failure of creating the user.
 */
export const CreateUser = async (request: ICreateUserRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.post('/Management/CreateUser', request);
        const message: string = response.data;
        return message;
    } catch (error) {
        throw error;
    }
}

/**
 * @category user
 * @remarks Sends all the information about a user through the api in order to update the user in the database.
 * @param request - information about a User entered when updating one as an admin from the UserAdmin pages. {@link IUpdateUserRequest}
 * @returns  - Message of success or failure of creating the user.
 */
export const UpdateUser = async (request: IUpdateUserRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.patch('/Management/UpdateUser', request);
        const message: string = response.data;
        return message;
    } catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category user
 * @remarks For users updating their own profile information. will also update the user's email in Auth0.
 * @param request - contains information to be updated for the logged in user's profile. {@link IUpdateUserProfileRequest}
 * @returns - message of success or failure
 */
export const UpdateUserProfile = async (request: IUpdateUserProfileRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.patch('/Management/UpdateUserProfile', request)
        const message: string = response.data;
        return message;
    } catch (error) {
        throw error;
    }
}

/**
 * @category user
 * @remarks The Given email will be sent to Auth0 which will send an email
 * to that email allowing the user to reset their password.
 * @param email - String containing the email of the user which
 *  should be sent the reset password email.
 * @returns - a message of success or failure
 */
export const ChangeUserPassword = async (email: string) => {
    try {
        const request = email;
        const instance = HttpClient();
        const response = await instance.post('/Auth0/ChangePassword?email=' + request);
        const message: string = response.data
        return message;
    } catch (error) {
        throw error
    }
}

/**
 * @category user
 * @remarks When called it will allow the user to reset only their own password
 * @returns - a message of success or failure
 */
export const ChangeProfilePassword = async () => {
    try {
        const instance = HttpClient();
        const response = await instance.post('/Auth0/ChangeProfilePassword');
        const message: string = response.data
        return message;
    } catch (error) {
        throw error
    }
}

/**
 * @category user
 * @remarks gets all of the grades from the grade table.
 * @returns - an array of all grades and their ids. {@link IGrade}
 */
export const GetAllGrades = async () => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/Management/GetAllGrades');
        const grades: IGrade[] = response.data;
        return grades;
    } catch (error) {
        throw error;
    }
};

/**
 * @category user
 * @remarks will get every role and their Id where the Role is lower permission than the logged in user
 * @example A Credentailing manager using this system would get a response of:
 * ```typescript
 * [
 * {id: 4, name: "Judge"},
 * {id: 5, name: "schoolCoordinator"},
 * {id: 6, name: "Youth"}
 * ]
 * ```
 * Notice that the sys admin roles as well as the Credentialing manager role are not brought back.
 * @returns - an Array of all Roles with lower permission than current logged in user.{@link IRole}
 */
export const GetRoles = async () => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/Management/GetAllRoles');
        const roles: IRole[] = response.data;
        return roles;
    } catch (error) {
        throw error;
    }
};

/**
 * @category tenant
 * @remarks Gets all of the tenants and their Ids from the tenant table.
 * @returns - array of all tenants {@link ITenantDetial}
 */
export const GetTenantList = async () => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/Management/GetAllTenants')
        const tenants: ITenantDetail[] = response.data
        return tenants;
    } catch (error) {
        throw error;
    }
}

/**
 * @category region
 * @remarks gets back all the basic information about all the regions.
 * @returns - array of all regions {@link IRegionDetail}
 */
export const GetRegionList = async () => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/Management/GetAllRegions')
        const regions: IRegionDetail[] = response.data;
        return regions;
    } catch (error) {
        throw error;
    }
}

/**
 * @category region
 * @params expression - expression for filtering the results.
 * @remarks gets back all the basic information about all the regions.
 * @returns - array of all regions {@link IRegionDetail}
 */
export const GetRegionListExpressionBuilder = async (expression: IExpressionRequest) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/Management/RegionExpressionBuilder', expression)
        const regions: IRegionPaginationResponse = response.data;
        return regions;
    } catch (error) {
        throw error;
    }
}

/**
 * @category region
 * @remarks sends a request of tenant id and gets back all the basic information about all the regions
 * that belong to that tenant id.
 * @param request - contains the tenant id to select regions from {@link IGetRegionsForTenantRequest}
 * @returns - array of all regions from a requested tenant. {@link IRegionDetail}
 */
export const GetRegionsForTenant = async (request: IGetRegionsForTenantRequest) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/Management/GetRegionsForTenant', request)
        const regions: IRegionDetail[] = response.data;
        return regions;
    } catch (error) {
        throw error;
    }
}

/**
 * @category region
 * @remarks sends a request containing the information about a region for it to be created.
 * @param request - a request containing the basic information about a region to be created {@link ICreateRegionRequest}
 * @returns - a string with status message from the API about execution outcome.
 */
export const CreateRegion = async (request: ICreateRegionRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.post('/Management/CreateRegion', request)
        const message: string = response.data
        return message;
    } catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category region
 * @remarks sends a request containing the information about a region for it to be updated.
 * @param request - a request containing the basic information about a region to be updated {@link IUpdateRegionRequest}
 * @returns - a string with status message from the API about execution outcome.
 */
export const UpdateRegion = async (request: IUpdateRegionRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.patch('/Management/UpdateRegion', request)
        const message: string = response.data
        return message;
    } catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category school
 * @remarks Gets all the schools in the system along with basic information
 * about them like email domain list, and their associated region's id. given an expression to filter by.
 * @returns - all schools in an array {@link ISchoolDetail}
 */
export const GetAllSchoolExpressionBuilder = async (request: IExpressionRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.post('/Management/SchoolExpressionBuilder', request);
        const schools: ISchoolPaginationResponse = response.data;
        return schools;
    } catch (error) {
        throw error;
    }
};

/**
 * @category school
 * @remarks Gets all the schools in the system along with basic information
 * about them like email domain list, and their associated region's id.
 * @returns - all schools in an array {@link ISchoolDetail}
 */
export const GetAllSchools = async () => {
    try {
        const instance = HttpClient();
        const response = await instance.get('/Management/GetAllSchools');
        const schools: ISchoolDetail[] = response.data;
        return schools;
    } catch (error) {
        throw error;
    }
};

/**
 * @category school
 * @remarks Gets all the schools in a given region along with basic information
 * about them like email domain list, and their associated region's id.
 * @param request - A request containing a region id to get schools for {@link ISchoolsForRegionRequest}
 * @returns - all schools for a region in an array {@link ISchoolDetail}
 */
export const GetSchoolsForRegion = async (request: ISchoolsForRegionRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.post('/Management/GetAllSchoolsInRegion', request);
        const schools: ISchoolDetail[] = response.data;
        return schools;
    } catch (error) {
        throw error;
    }
};

/**
     * @category school
     * @remarks
     * Creates an instance of HttpClient, and calls the API endpoint CreateSchool,
     * it then returns a response to indicate either a successful operation
     * or show an error message.
     * @param  request - an object with the parameters: {@link ICreateSchoolRequest}
     * @returns - execution status from the API.
     */
export const CreateSchool = async (request: ICreateSchoolRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.post('/Management/CreateSchool', request);
        const message: string = response.data;
        return message;
    }
    catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
     * @category school
     * @remarks
     * Creates an instance of HttpClient, and calls the API endpoint UpdateSchool,
     * it then returns a response to indicate either a successful operation
     * or show an error message.
     * @param  request - an object with the parameters: {@link UpdateSchoolRequest}
     * @returns - execution status from the API.
     */
export const UpdateSchool = async (request: IUpdateSchoolRequest) => {
    try {
        const instance = HttpClient();
        const response = await instance.patch('/Management/UpdateSchool', request);
        const message: string = response.data;
        return message;
    }
    catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category youth
 * @remarks sends a request as user id to get a youth's submissions to be displayed on UI.
 * @param request - request containing the Id of the Youth for which the dashboards information
 * will be filled in. {@link IYouthDashboardInfoRequest}
 * @returns - the list of submissions for that user id. {@link IYouthDashboardInfo}
 */
export const GetYouthDashboardInfo = async (request?: number) => {
    try {
        const instance = HttpClient();
        if (request) {
            let response = await instance.get('/UserExperience/GetUserDashboardInformation?userId=' + request)
            const submissionList: IYouthDashboardInfo = response.data;
            return submissionList;
        }
        else {
            let response = await instance.get('/UserExperience/GetUserDashboardInformation')
            const submissionList: IYouthDashboardInfo = response.data;
            return submissionList;
        }
    } catch (error) {
        throw error;
    }
}

/**
 * @category youth
 * @remarks sends a file to the API for storing it in the database.
 * @param form - pdf submission either the draft or final submission.
 * @returns - :)
 */
export const submitFile = async (form: FormData) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/UserExperience/SubmitFile', form);
        const message: string = response.data;
        return message;
    } catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category youth
 * @remarks Uses the competency Id to get the submission file for the user.
 * @param request - the competency id for the submission you are trying to get, and if the user
 * is not a school coordinator it sends the userId for the user you are trying to retrieve the
 * submission for.
 * @returns message of status of operation
 */
export const GetSubmission = async (competencyId: number, userId?: number) => {
    try {
        const instance = HttpClient();
        if (userId) {
            let response = await instance.get('/UserExperience/GetSubmission?competencyId=' + competencyId + "&userId=" + userId, { responseType: 'blob' });
            return response.data;
        }
        else {
            let response = await instance.get('/UserExperience/GetSubmission?competencyId=' + competencyId, { responseType: 'blob' });
            return response.data;
        }
    } catch (error) {
        throw error;
    }
}

/**
 * @category youth
 * @remarks provides the user with a badge they can see on the submission page
 * when they have been awarded the badge for that competency.
 * @param request - the competency Id for the badge you want to get and the userId of that user.
 * @returns - png of badge
 */
export const GetBadge = async (competencyId: number, userId?: number) => {
    try {
        const instance = HttpClient();
        if (userId) {
            let response = await instance.get('/UserExperience/GetCompetencyBadge?competencyId=' + competencyId + '&userId=' + userId, { responseType: 'blob' });
            return response.data;
        }
        else {
            let response = await instance.get('/UserExperience/GetCompetencyBadge?competencyId=' + competencyId, { responseType: 'blob' });
            return response.data;
        }
    } catch (error) {
        throw error;
    }
}

/**
 * @category youth
 * @remarks returns the certificate a Youth recieves when they have completed
 * all of the competencies, been awarded the badge for each, and press the get badge button.
 * @returns - pdf certificate with the Youth's name and all the badges.
 */
export const GetCertificate = async () => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/UserExperience/GetCertificate', { responseType: 'blob' });
        return response.data;
    } catch (error) {
        throw error;
    }
}

/**
 * @category school coordinator
 * @remarks Array of all the youth's and their info for filling into a table of
 * Youths on school coordinator dashboard.
 * @param request - schoolId of the school you want to get users from
 * @param expression - search query of user's names.
 * @returns -all the SC and Youths for the given schoolId. {@link IUsersForSchool}
 */
export const GetUserForSchool = async (request: number, expression: IExpressionRequest) => {
    try {
        const instance = HttpClient();
        let response = await instance.post("/UserExperience/GetUsersBySchool?schoolId=" + request, expression);
        const users: IUsersForSchoolPaginatedResponse = response.data;
        return users;
    } catch (error) {
        throw error;
    }
}

/**
 * @category judge
 * @remarks Gets a list of all judges for a given region for a drop down of judges available
 * to judge a Youth's submission.
 * @param request - the region Id of the Youth for which to select all judges and
 * Credentialing managers within that region.
 * @returns - list of Judges within the Youth's region {@link IJudgeDetail}
 */
export const GetJudgesByRegion = async (region: number, judge1Id: number, judge2Id?: number) => {
    try {
        const instance = HttpClient();
        if (judge2Id) {
            let response = await instance.get(`/UserExperience/GetJudgesByRegion?regionId=${region}&judgeUserId=${judge1Id}&judge2UserId=${judge2Id}`);
            const users: IJudgeDetail[] = response.data;
            return users;
        }
        else {
            let response = await instance.get(`/UserExperience/GetJudgesByRegion?regionId=${region}&judgeUserId=${judge1Id}`);
            const users: IJudgeDetail[] = response.data;
            return users;
        }
    } catch (error) {
        throw error;
    }
}

/**
 * @category judge
 * @remarks Gets a submissions updated data after being judged given the submissionId.
 * @param request - the submission Id the details should be returned for.
 * @returns - submission Details for one submissionId {@link ICaseDetailInformation}
 */
export const GetSubmissionById = async (request: number) => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/UserExperience/GetSubmissionById?submissionId=' + request);
        const caseDetail: ICaseDetailInformation = response.data;
        return caseDetail;
    } catch (error) {
        throw error;
    }
}

/**
 * @category judge
 * @remarks Sets the review start date time of the given submissionId
 * @param request - the submission Id the review start date time should be set for
 * @returns - message of successful operations.
 */
export const SetReviewStartDate = async (request: number) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/UserExperience/SetReviewStartDate?submissionId=' + request);
        const message: string = response.data
        return message;
    } catch (error) {
        throw error;
    }
}

/**
 * @category judge
 * @remarks Gets an object containing a count of all the cases a Judge,
 *  Credentialing manager, or System Administrator can see.
 * @returns - Data for the Admin Dashboard Page (counts for all cases) {@link IAdminDashboardData}
 */
export const GetAdminDashboardData = async () => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/UserExperience/GetAdminDashboardData');
        const data: IAdminDashboardData = response.data;
        return data;
    } catch (error) {
        throw error;
    }
}

/**
 * @category judge
 * @params expression - expression for filtering the results.{@link ICaseDetailExpressionRequest}
 * @remarks gets case detail data based on the expression sent back, to be displayed in a table
 * on the case detail page.
 * @returns - array of objects containing the submissions to be displayed.
 */
export const GetCaseDetailData = async (expression: ICaseDetailExpressionRequest) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/UserExperience/GetCaseDetailData', expression)
        const data: ICaseDetail = response.data;
        return data;
    } catch (error) {
        throw error;
    }
}

/**
 * @category user
 * @params expression - expression for filtering the results.{@link IRequestedExpressions}
 * @remarks gets Excel file containing data dump of submissions in the selected time Range
 * @returns - Excel file of submissions.
 */
export const DownloadUserDataExpressionBuilder = async (expression: IRequestedExpressions) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/Management/DataDumpExpressionBuilder', expression, { responseType: 'blob' })
        return response.data;
    } catch (error) {
        throw error;
    }
}

/**
 * @category user
 * @params expression - expression for filtering the results.{@link IRequestedExpressions}
 * @remarks gets Excel file containing view count info in the selected range Range
 * @returns - Excel file of sponsored ad view counts.
 */
export const DownloadViewCountReport = async (expression: IRequestedExpressions) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/Management/ViewCountReportExpressionBuilder', expression, { responseType: 'blob' })
        return response.data;
    } catch (error) {
        throw error;
    }
}

/**
 * @category judge
 * @params request - Information For Assigning a Judge{@link IJudgeAssignmentRequest}
 * @remarks Assigns the Judge User Id Provided to be a Judge for the submission Id provided.
 * @returns - message of successful operation
 */
export const AssignJudgeToSubmission = async (request: IJudgeAssignmentRequest) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/UserExperience/AssignJudgeToSubmission', request)
        const message: string = response.data;
        return message;
    } catch (error) {
        throw error;
    }
}

/**
 * @category judge
 * @params request - Information For Judging a Submission{@link IJudgeSubmissionRequest}
 * @remarks Judges the given submission with based on isAwarded boolean value returned and adds feedback.
 * @returns - message of successful operation
 */
export const JudgeSubmission = async (request: IJudgeSubmissionRequest) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/UserExperience/JudgeSubmission', request)
        const message: string = response.data;
        return message;
    } catch (error) {
        throw error;
    }
}

/**
 * @category user
 * @remarks for an MSA to look at and view all of the ads currently in circulation.
 * @returns - a List of all ads for the filled SlotNumbers.
 */
export const GetSponsoredAds = async () => {
    try {
        const instance = HttpClient();
        let response = await instance.get('/Management/GetSponsoredAds');
        const sponsoredAds: ISponsoredAdsUpper[] = response.data;
        return sponsoredAds;
    } catch (error: any) {
        const errorMessage = error.response.data || 'an error occured when getting Ads';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category user
 * @remarks for an MSA to upload an ad.
 * @returns - a message of successful operations.
 */
export const UploadSponsoredAd = async (form: FormData) => {
    try {
        const instance = HttpClient();
        let response = await instance.post('/Management/UploadSponsoredAd', form);
        const message: string = response.data;
        return message;
    } catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category user
 * @param request - Optional slot number parameter.
 * @remarks for Displaying an ad on the screens throughout the app.
 * @returns - an ad for a given slot number or a random ad if no slot number is given.
 */
export const GetAdForDisplaying = async (request?: number) => {
    try {
        const instance = HttpClient();
        let sponsoredAd: ISponsoredAds;
        if (request) {
            let response = await instance.get('/Management/GetAdForDisplaying?slotNumber=' + request);
            sponsoredAd = response.data;
        }
        else {
            let response = await instance.get('/Management/GetAdForDisplaying');
            sponsoredAd = response.data;
        }
        return sponsoredAd;
    } catch (error: any) {
        const errorMessage = error.response.data || 'An error occurred';
        const customError = new Error(errorMessage);
        throw customError;
    }
}

/**
 * @category user
 * @param request - Ad Id for which to update the ViewCount record of.
 * @remarks Updates the ViewCount for a Specific Ad, if the Record Doesn't
 * exist it will create the record.
 * @returns - message of successful operation.
 */
export const UpdateAdViewCount = async (request: number) => {
    try {
        const instance = HttpClient();
        let response = await instance.put('/Management/UpdateAdViewCount?sponsoredAdId=' + request)
        const message: string = response.data;
        return message;
    } catch (error) {
        throw error;
    }
}
/**
 * @category user
 * @param request - Ad Id for which to update the ClickCount record of.
 * @remarks Updates the ClickCount for a Specific Ad
 * @returns - message of successful operation.
 */
export const UpdateAdClickCount = async (request: number) => {
    try {
        const instance = HttpClient();
        let response = await instance.put('/Management/UpdateAdClickCount?sponsoredAdId=' + request)
        const message: string = response.data;
        return message;
    } catch (error) {
        throw error;
    }
}
